import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavbarProps {
  scrolled?: boolean;
}

export default function Navbar({ scrolled: scrolledProp }: NavbarProps) {
  const [internalScrolled, setInternalScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (scrolledProp !== undefined) return;
    const handleScroll = () => setInternalScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolledProp]);

  const scrolled = scrolledProp !== undefined ? scrolledProp : internalScrolled;

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const isHome = location.pathname === '/' || location.pathname === '';

    if (isHome) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 400);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 lg:px-8">
        <div className="flex items-center h-16 lg:h-24 gap-4 lg:gap-6">
          {/* Logo */}
          <div className="flex items-center cursor-pointer flex-shrink-0" onClick={() => scrollToSection('hero')}>
            <img
              src="https://public.readdy.ai/ai/img_res/5c94f762-ca99-4c14-8d6b-1e4009698ef7.png"
              alt="All Phases Builders LLC"
              className="h-12 lg:h-20 w-auto"
            />
          </div>

          {/* Desktop Navigation - Centered using flex-1 */}
          <div className="hidden lg:flex flex-1 items-center justify-center gap-8">
            <button
              onClick={() => scrollToSection('hero')}
              className={`text-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-brand' : 'text-white hover:text-brand-light'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-brand' : 'text-white hover:text-brand-light'
              }`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className={`text-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-brand' : 'text-white hover:text-brand-light'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('portfolio')}
              className={`text-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-brand' : 'text-white hover:text-brand-light'
              }`}
            >
              Portfolio
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-brand' : 'text-white hover:text-brand-light'
              }`}
            >
              Contact
            </button>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <a
              href="tel:+18605905605"
              className={`text-base font-medium transition-colors whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-brand' : 'text-white hover:text-brand-light'
              }`}
            >
              <i className="ri-phone-line mr-1"></i>
              (860) 590-5605 / (872) 204-4484
            </a>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-brand text-white px-6 py-2.5 rounded-lg text-base font-medium hover:bg-brand-dark transition-all whitespace-nowrap cursor-pointer"
            >
              Request Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 ml-auto"
          >
            <i className={`text-2xl ${scrolled ? 'text-gray-900' : 'text-white'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-6 py-4 space-y-3">
            <button onClick={() => scrollToSection('hero')} className="block w-full text-left text-gray-700 hover:text-brand py-2 whitespace-nowrap cursor-pointer">Home</button>
            <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-700 hover:text-brand py-2 whitespace-nowrap cursor-pointer">About</button>
            <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-700 hover:text-brand py-2 whitespace-nowrap cursor-pointer">Services</button>
            <button onClick={() => scrollToSection('portfolio')} className="block w-full text-left text-gray-700 hover:text-brand py-2 whitespace-nowrap cursor-pointer">Portfolio</button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-brand py-2 whitespace-nowrap cursor-pointer">Contact</button>
            <button onClick={() => scrollToSection('contact')} className="w-full bg-brand text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-all whitespace-nowrap cursor-pointer">Request Quote</button>
          </div>
        </div>
      )}
    </nav>
  );
}
